"use client";

import {
  motion,
  useScroll,
  useTransform,
  useAnimation,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { useRef, useEffect, useState } from "react";
import Image from "next/image";
import { ChevronRight } from "lucide-react";

const Hero = () => {
  const ref = useRef(null);
  const controls = useAnimation();
  const [animationComplete, setAnimationComplete] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgScale = useMotionValue(10);
  const dynamicBgScale = useTransform(scrollYProgress, [0, 1], [1.5, 10]);
  const smoothBgScale = useSpring(bgScale, { stiffness: 100, damping: 30 });
  const bgX = useTransform(scrollYProgress, [0, 1], ["24.25%", "-20%"]);
  const smoothBgX = useSpring(bgX, {
    stiffness: 100,
    damping: 30,
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["5.5%", "50%"]);
  const smoothBgY = useSpring(bgY, {
    stiffness: 100,
    damping: 30,
  });

  const bentukScale = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const smoothBentukScale = useSpring(bentukScale, {
    stiffness: 100,
    damping: 30,
  });
  const bentukX = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const smoothBentukX = useSpring(bentukX, {
    stiffness: 100,
    damping: 30,
  });

  const titleScale = useTransform(scrollYProgress, [0, 1], [2, 0]);
  const smoothTitleScale = useSpring(titleScale, {
    stiffness: 100,
    damping: 30,
  });
  const titleX = useTransform(scrollYProgress, [0, 1], ["49%", "-49%"]);
  const smoothTitleX = useSpring(titleX, { stiffness: 100, damping: 30 });

  const breadcrumbOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const smoothBreadcrumbOpacity = useSpring(breadcrumbOpacity, {
    stiffness: 100,
    damping: 30,
  });

  useEffect(() => {
    controls
      .start({
        scale: 1.5,
        transition: { duration: 1.5, ease: "easeInOut" },
      })
      .then(() => {
        setAnimationComplete(true);
        bgScale.set(dynamicBgScale.get());
      });
  }, [controls, bgScale, dynamicBgScale]);

  useEffect(() => {
    if (animationComplete) {
      const unsubscribe = dynamicBgScale.on("change", (value) => {
        bgScale.set(value);
      });

      return () => unsubscribe();
    }
  }, [animationComplete, dynamicBgScale, bgScale]);

  return (
    <div
      ref={ref}
      className="relative flex flex-col w-full h-screen justify-center items-start gap-1 px-5 md:px-10 lg:px-16 py-20 overflow-hidden bg-[#0E1C29]"
    >
      {/* Background with zoom effect */}
      <motion.div
        className="absolute inset-0 bg-cover bg-no-repeat :
        bg-[position:-180px_-55px] 
        sm:bg-[position:-135px_-55px] 
        md:bg-[position:-136px_-140px] 
        lg:bg-right"
        style={{
          backgroundImage: "url('/image/bg-hero-filosofi2.avif')",
          scale: smoothBgScale,
          x: smoothBgX,
          y: smoothBgY,
        }}
        initial={{ scale: 10 }}
        animate={controls}
      />

      {/* Dark overlay */}
      {/* <div className="absolute inset-0 bg-gradient-to-t from-[#2A2E66B8] from-40% to-transparent"></div> */}
      <motion.div
        className="absolute inset-0 bg-black"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{
          duration: 1,
        }}
      />

      <div className="flex flex-col lg:flex-row w-full gap-5 lg:gap-20 z-10 items-stretch">
        <div className="flex flex-col justify-end w-full lg:w-1/2">
          {/* Breadcrumb text */}
          <motion.div
            className="relative flex flex-row items-center text-base md:text-lg text-left font-light text-[#F8F8F8] mb-5"
            style={{ opacity: smoothBreadcrumbOpacity }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 1.2,
              ease: "easeInOut",
              delay: 2,
            }}
          >
            Beranda <ChevronRight size={16} className="inline mx-1" /> Filosofi Logo
          </motion.div>

          {/* Title with enlarging effect */}
          <motion.div
            className="relative text-xl md:text-3xl lg:text-4xl text-left text-white font-bold"
            style={{ scale: smoothTitleScale, x: smoothTitleX }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 1.2,
              ease: "easeInOut",
              type: "spring",
              stiffness: 100,
              damping: 15,
              delay: 1.2,
            }}
            viewport={{ once: true }}
          >
            Filosofi Logo
          </motion.div>
        </div>
        <motion.div
          className="flex flex-col justify-between items-center w-full lg:w-1/2 p-7 lg:p-10 gap-5 md:gap-10 bg-white rounded-2xl"
          style={{ scale: smoothBentukScale, x: smoothBentukX }}
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            ease: "easeInOut",
            type: "spring",
            stiffness: 100,
            damping: 15,
            delay: 1.8,
          }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 2 }}
            animate={{
              opacity: [0, 1, 1],
              y: [100, 100, 0],
              scale: [1.5, 1.5, 1],
            }}
            transition={{
              duration: 1.6,
              ease: "easeInOut",
              times: [0, 0.7, 1],
              delay: 2.6,
            }}
            viewport={{ once: true }}
            className="flex justify-center items-center w-full md:w-2/5"
          >
            <div className="relative w-[150px] md:w-[280px] aspect-[1/1] mx-auto lg:mx-0">
              <Image
                src="/image/logoUmkm1.webp"
                alt="logo umkm"
                fill
                className="object-contain"
                sizes="(max-width: 768px) 150px, 280px"
              />
            </div>
          </motion.div>
          <div className="flex flex-col w-full space-y-5 text-center lg:text-left gap-0">
            <motion.h3
              className="text-cust-blue font-semibold text-lg md:text-xl lg:text-2xl text-center"
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                ease: "easeInOut",
                type: "spring",
                stiffness: 100,
                damping: 15,
                delay: 4.2,
              }}
              viewport={{ once: true }}
            >
              Bentuk dan Warna Logo
            </motion.h3>
            <motion.p
              className="font-light text-xs md:text-sm lg:text-base text-justify"
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                ease: "easeInOut",
                type: "spring",
                stiffness: 100,
                damping: 15,
                delay: 4.4,
              }}
              viewport={{ once: true }}
            >
              Logo Kementerian Usaha Mikro, Kecil, dan Menengah terdiri dari 4
              (empat) elemen, yakni lilin penerang, panah kembar, suar abadi,
              dan roda cahaya. Elemen-elemen tesebut memiliki makna bahwa UMKM
              seperti lilin-lilin kecil yang menyala di setiap pelosok negeri,
              menciptakan jaringan cahaya yang menyalakan harapan, menciptakan
              inovasi, dan menjaga roda ekonomi terus berputar.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Hero;
